import { useMemo } from 'react';

import {
  RACHEL_COMPACTING_DATA_PART,
  type RachelCompactingPayload,
  RachelCompactingPayloadSchema,
} from './rachelCompacting.schema';
import { UI_EFFECT_PART_TYPE, type UiEffect, UiEffectSchema } from './uiEffect.schema';
import { useRachelCompactingHandler } from './useRachelCompactingHandler';
import { useUiEffectHandler } from './useUiEffectHandler';

export type RachelDataPart = {
  type: string;
  data: unknown;
  transient?: boolean;
};

interface CreateRachelDataHandlerParams {
  handleUiEffect: (effect: UiEffect) => void | Promise<void>;
  handleCompacting: (payload: RachelCompactingPayload) => void;
  warn: (message: string, error: unknown) => void;
}

interface HandleCompactingDataPartParams {
  dataPart: RachelDataPart;
  handleCompacting: (payload: RachelCompactingPayload) => void;
  warn: (message: string, error: unknown) => void;
}

const handleCompactingDataPart = ({
  dataPart,
  handleCompacting,
  warn,
}: HandleCompactingDataPartParams): void => {
  const parsed = RachelCompactingPayloadSchema.safeParse(dataPart.data);

  if (!parsed.success) {
    warn('[rachel] compacting payload inválido', parsed.error);
    return;
  }

  handleCompacting(parsed.data);
};

export const createRachelDataHandler = ({
  handleUiEffect,
  handleCompacting,
  warn,
}: CreateRachelDataHandlerParams) => {
  let effectQueue = Promise.resolve();

  return (dataPart: RachelDataPart): void => {
    if (dataPart.type === RACHEL_COMPACTING_DATA_PART) {
      handleCompactingDataPart({ dataPart, handleCompacting, warn });
      return;
    }

    if (dataPart.type !== UI_EFFECT_PART_TYPE) {
      return;
    }

    const parsed = UiEffectSchema.safeParse(dataPart.data);

    if (!parsed.success) {
      warn('[rachel] ui effect inválido', parsed.error);
      return;
    }

    effectQueue = effectQueue
      .then(() => handleUiEffect(parsed.data))
      .catch((error) => {
        warn('[rachel] erro ao processar ui effect', error);
      });
  };
};

export const useRachelDataHandler = () => {
  const handleUiEffect = useUiEffectHandler();
  const handleCompacting = useRachelCompactingHandler();

  return useMemo(
    () =>
      createRachelDataHandler({
        handleUiEffect,
        handleCompacting,
        warn: console.warn,
      }),
    [handleUiEffect, handleCompacting],
  );
};
